import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import Emojicon from 'components/generic/Emojicon.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import DEPOSIT_AMOUNT from 'constants/salon/depositAmount.const';
import S from 'constants/string.const';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonNoShowProtection } from 'selectors/currentSalon.selectors';
import fromAnyToUpperCase from 'utils/converters/any/fromAnyToUpperCase.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToInitials from 'utils/converters/string/fromAnyToInitials.util';
import fixed from 'utils/values/fixed.util';

import NotesContainer from 'clientProfile/components/BasicProfile/Notes';
import ShowTags from 'clientProfile/components/BasicProfile/ShowTags';

const BasicProfileCounter = ({ data, title }) => (
  <div data-bem="BasicProfileCounter">
    <small className="counter-text">{title}</small>
    <span>{data}</span>
  </div>
);

const Details = ({
  bookingStatusReport,
  client,
  clientDepositData,
  clientTags,
  goToAddCredit,
  goToEditDetails,
  goToPatchView,
  goToViewDeposits,
  hasNoShowProtection,
  ncobDepositSettings,
  remainingHistoryBookings,
}) => {
  const { t } = useTranslation();
  const [totalHistoryBookings, setTotalHistoryBookings] = useState(remainingHistoryBookings);
  const displayCardNumber = !hasNoShowProtection && ncobDepositSettings === DEPOSIT_AMOUNT.empty;

  useEffect(() => {
    if (remainingHistoryBookings && !totalHistoryBookings) {
      setTotalHistoryBookings(remainingHistoryBookings);
    }
  }, [remainingHistoryBookings, totalHistoryBookings]);

  const patchDate = client.patch_test_date;
  // FIXME: improve code and css classes

  // TODO: use utility function for formatting, not the moment library directly
  const months = moment().diff(moment(patchDate), 'days');

  const abrv = fromAnyToUpperCase(fromAnyToInitials(client?.user_name));
  const h3Txt =
    client.known_as ||
    (client.birthday ? `Birthday ${client.birthday[0]} ${moment.monthsShort(client.birthday[1] - 1)}` : '');

  const balanceAmount =
    clientDepositData.response && clientDepositData.response.deposits_amount
      ? clientDepositData.response.deposits_amount.total
      : 0;

  // TODO: refactor this to use const, not let
  let buttonStyle;
  if (balanceAmount) {
    buttonStyle = {
      display: 'flex',
      justifyContent: 'center',
    };
  } else {
    buttonStyle = {
      display: 'flex',
      justifyContent: 'center',
    };
  }

  return (
    <div className="bw-client-header">
      <div className="client-initials">
        <span>{abrv}</span>
      </div>
      <span />

      <div>
        <h2 className="f-b">{client.user_name}</h2>
        <div className="client-prompt-buttons 1">
          <div className="prompt-item">
            {client.phone ? null : (
              <div className="prompt" tabIndex="0" role="button" onClick={goToEditDetails}>
                {t('composite.clientProfile.details.noPhone')}
              </div>
            )}
          </div>
          {!client.phone && <div className="prompt-item dot">•</div>}

          <div className="prompt-item">
            {client.birthday ? (
              h3Txt
            ) : (
              <div className="prompt" tabIndex="0" role="button" onClick={goToEditDetails}>
                {t('composite.clientProfile.details.noBirthday')}
              </div>
            )}
          </div>
          <div className="prompt-item dot">•</div>
          <div className="prompt-item">
            {client.patch_test_date ? (
              <>
                {months >= 182 ? (
                  // date is in past -  expired
                  <div className="prompt" tabIndex="0" role="button" onClick={goToPatchView}>
                    {t('composite.clientProfile.details.patchTestExpired')}
                  </div>
                ) : (
                  <Space>
                    {t('composite.clientProfile.details.patchTestTitle')} {moment(patchDate).format('D MMM YYYY')}
                  </Space>
                )}
              </>
            ) : (
              <div
                className="prompt"
                tabIndex="0"
                role="button"
                onClick={() => {
                  goToPatchView();
                }}
              >
                {t('composite.clientProfile.details.noPatchTest')}
              </div>
            )}
          </div>
        </div>
      </div>
      {fromAnyToBool(clientTags) && <ShowTags tagList={clientTags} />}
      {client.notes && <NotesContainer notes={client.notes} />}
      <button tabIndex="0" onClick={goToEditDetails} className="btn btn-violet">
        {t('composite.clientProfile.details.editRecordButton')}
      </button>
      <button tabIndex="0" onClick={goToEditDetails} className="btn btn-violet">
        {t('composite.clientProfile.details.addTagsButton')}
        {S.space}
        <Emojicon description="party face" value={S.partyingFace} />
      </button>
      <div className="client-profile-counter">
        <BasicProfileCounter
          title={t('composite.clientProfile.details.noShows')}
          data={bookingStatusReport.all_no_shows}
        />
        <BasicProfileCounter
          title={t('composite.clientProfile.details.cancellations')}
          data={bookingStatusReport.all_canceled}
        />
        <BasicProfileCounter title={t('composite.clientProfile.details.pastBookings')} data={totalHistoryBookings} />
        <BasicProfileCounter
          title={t('composite.clientProfile.details.upcomingBookings')}
          data={bookingStatusReport.upcoming_appointments}
        />
      </div>
      <div className="client-profile-counter second-row">
        <BasicProfileCounter
          title={t('composite.clientProfile.details.cardOnFile')}
          data={displayCardNumber ? <div>-</div> : client.active_card}
        />

        {clientDepositData?.response && (
          <BasicProfileCounter
            title={t('composite.clientProfile.details.totalDeposit')}
            data={<CurrencySign value={balanceAmount ? fixed(balanceAmount) : 0} />}
          />
        )}
      </div>
      <div style={buttonStyle}>
        <div role="button" tabIndex={-1} className="btn btn-violet view-deposits" onClick={goToAddCredit}>
          {t('composite.clientProfile.details.addCreditButton')}
        </div>
        {balanceAmount > 0 && (
          <div
            role="button"
            tabIndex={0}
            className="btn btn-violet view-deposits"
            onClick={() => goToViewDeposits(client.id)}
          >
            {t('composite.clientProfile.details.viewDepositsButton')}
          </div>
        )}
      </div>
    </div>
  );
};

const DetailsHoc = connect((state) => ({
  hasNoShowProtection: selectCurrentSalonNoShowProtection(state),
  ncobDepositSettings: state?.currentSalon?.ncob_deposit_settings,
}))(withTranslation()(Details));

export default DetailsHoc;
